import type { Marked, Token } from 'marked';

/**
 * Renders a markdown text that grows chunk by chunk. Every top-level block before the last non-blank
 * one is final, so it is rendered once and kept; each chunk re-lexes only from that last block on.
 *
 * A few constructs can still differ from a full parse, because a frozen block never sees text that
 * arrives later: an ordered list continued after a blank line, or a reference link defined further
 * down. Callers should render the settled text with a full parse.
 */
export class StreamingMarkdown {
  private frozenSource = '';
  private frozenHtml = '';

  constructor(private readonly markdown: Marked) {}

  render(text: string): string {
    // The lexer turns CRLF into LF, so token raws only add up to the source once it has done the same.
    const source = text.replace(/\r\n?/g, '\n');
    if (!source.startsWith(this.frozenSource)) {
      this.frozenSource = '';
      this.frozenHtml = '';
    }
    const tokens: Token[] = this.markdown.lexer(source.slice(this.frozenSource.length));
    let open = tokens.length - 1;
    while (open > 0 && tokens[open]!.type === 'space') open--;
    if (open > 0) {
      const done = tokens.splice(0, open);
      this.frozenSource += done.map((t) => t.raw).join('');
      this.frozenHtml += this.markdown.parser(done);
    }
    return this.frozenHtml + this.markdown.parser(tokens);
  }
}
